import styled, { css } from 'styled-components';
import { exFont } from '../../shared/typography';
import Pill from '../../shared/ui/Pill';
import { Card, StepColumn, Sub } from '../QuestionsStep/style';

export const MarkingColumn = styled(StepColumn)`
  max-width: 912px;
  gap: 14px;
  & > :first-child {
    gap: 6px;
    h2 {
      line-height: normal;
    }
    p {
      font-size: 14.5px;
      line-height: normal;
      color: var(--ex-ink-2);
    }
  }
  & > section:first-of-type {
    padding: 14px 20px;
  }
`;

export const MarkingCard = styled(Card)`
  position: relative;
  gap: 18px;
  padding-left: 20px;
  padding-right: 20px;
  & > p {
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: -4px;
  }
`;

// Marking step (tplA 585–763). Form primitives are shared with Questions.
export * from '../QuestionsStep/style';

// Total marks (tplA 588–590): 600 16px label; single = 76×50 input, 1.5px
// border, 700 22px; multi = a #F3F0F8 box then "Sum of all questions · …".
export const TotalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  & > span:first-child,
  label {
    font-size: 16px;
  }
  & > div {
    gap: 14px;
  }
  input {
    box-sizing: border-box;
    width: 76px;
    height: 50px;
    padding: 0;
    border: 1.5px solid
      ${({ $invalid }) =>
        $invalid ? 'var(--ex-bad)' : 'var(--ex-line-strong)'};
    border-radius: 12px;
    font-size: 22px;
    font-weight: 700;
  }
`;

export const TotalValue = styled.span`
  ${exFont(700, 22)}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 60px;
  height: 50px;
  padding: 0 14px;
  border-radius: 12px;
  background: #f3f0f8;
  color: var(--ex-ink);
`;

export const TotalSum = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

export const TotalNote = styled.span`
  ${exFont(400, 's')}
  color: var(--ex-ink-2);
  & > button {
    font-size: inherit;
    vertical-align: baseline;
  }
  & > button:hover:not(:disabled) {
    text-decoration: none;
  }
`;

// "Two more tabs to check" (tplA 592–598): a dark popover under the tabs.
export const TipCard = styled.div`
  position: absolute;
  z-index: 20;
  top: 57px;
  left: 95px;
  width: 340px;
  max-width: calc(100% - 24px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 16px 18px 14px;
  border-radius: 14px;
  background: var(--ex-ink);
  color: var(--white);
  box-shadow: 0 14px 36px rgba(37, 34, 42, 0.28);
  &::before {
    content: '';
    position: absolute;
    top: -7px;
    left: 34px;
    width: 14px;
    height: 14px;
    background: var(--ex-ink);
    transform: rotate(45deg);
    border-radius: 2px;
  }
  & > p {
    ${exFont(400, 13.5)}
    line-height: 1.5;
    margin: 6px 0 0;
    color: #e9e6ef;
  }
  & > div:first-child + p {
    margin-top: 8px;
  }
  & > p b {
    font-weight: 700;
    color: var(--white);
  }
`;

export const TipHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  & > p {
    ${exFont(700, 15)}
    line-height: normal;
    margin: 0;
    flex: 1;
    color: var(--white);
  }
  & > button {
    display: flex;
    padding: 0;
    border: none;
    background: none;
    color: var(--white);
    opacity: 0.8;
    cursor: pointer;
  }
`;

export const TipFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
  & > span:first-child {
    ${exFont(500, 12)}
    flex: 1;
    color: var(--ex-ink-faint);
  }
  & > button:nth-of-type(1) {
    ${exFont(600, 13.5)}
    padding: 0;
    border: none;
    background: none;
    color: #e9e6ef;
    cursor: pointer;
  }
  & > button:nth-of-type(2) {
    ${exFont(600, 13.5)}
    height: 34px;
    padding: 0 16px;
    border: none;
    border-radius: 999px;
    background: var(--white);
    color: var(--ex-royal);
    cursor: pointer;
  }
`;

export const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin: -20px -20px 0;
  padding: 0 12px;
  overflow-x: auto;
  border-bottom: 1px solid var(--ex-line);
`;

// Tabs (tplA 600–606): 600 15px, 2.5px underline, 8px to the dot and note.
export const Tab = styled.button`
  ${exFont(600, 15)}
  line-height: normal;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 12px 12px;
  border: none;
  border-bottom: 2.5px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'transparent')};
  background: none;
  color: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-ink-strong)'};
  white-space: nowrap;
  cursor: pointer;
`;

export const TabNote = styled.span`
  ${exFont(500, 12.5)}
  line-height: normal;
  color: var(--ex-ink-muted);
`;

export const UnseenDot = styled.span`
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: var(--ex-purple);
  box-shadow: 0 0 0 3px #ede3fd;
`;

export const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// One rubric row per leaf (tplA 611): suggested rows are tinted.
export const ToolRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid
    ${({ $suggested }) => ($suggested ? '#d9c6fb' : 'var(--ex-line)')};
  border-radius: 12px;
  background: ${({ $suggested }) =>
    $suggested ? '#fbf8fe' : 'var(--ex-surface)'};
  & > button {
    ${exFont(600, 13.5)}
    height: 38px;
    padding: 0 16px;
    white-space: nowrap;
  }
`;

export const RowLabel = styled.span`
  ${exFont(700, 15)}
  line-height: normal;
  min-width: 48px;
  color: var(--ex-royal);
`;

export const RowMain = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const RowTitle = styled.p`
  ${exFont(600, 's')}
  font-size: 14px;
  line-height: normal;
  margin: 0;
  color: var(--ex-ink);
`;

export const RowMeta = styled.p`
  ${exFont(400, 13)}
  line-height: normal;
  margin: 0;
  color: var(--ex-ink-2);
`;

export const RowIssue = styled.p`
  ${exFont(500, 'xs')}
  margin: 2px 0 0;
  color: ${({ $advisory }) =>
    $advisory ? 'var(--ex-warn-ink)' : 'var(--ex-bad-ink)'};
`;

// The small confirm dialog of tplA 731 (470px, no header rule, link + pill).
export const SmallDialogScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(37, 34, 42, 0.55);
`;

export const SmallDialog = styled.div`
  box-sizing: border-box;
  width: 470px;
  max-width: 100%;
  padding: 24px 26px;
  border-radius: 16px;
  background: var(--ex-surface);
  box-shadow: var(--ex-shadow-modal);
  &:focus {
    outline: none;
  }
  & > h3 {
    ${exFont(700, 18)}
    line-height: normal;
    margin: 0 0 8px;
    color: var(--ex-ink);
  }
  & > p {
    ${exFont(400, 14)}
    line-height: 1.55;
    margin: 0 0 20px;
    color: var(--ex-ink-strong);
  }
`;

export const SmallDialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  & > button {
    ${exFont(600, 14)}
    cursor: pointer;
  }
  & > button:first-child {
    padding: 0;
    border: none;
    background: none;
    color: var(--ex-purple);
  }
  & > button:last-child {
    height: 40px;
    padding: 0 20px;
    border: none;
    border-radius: 999px;
    background: var(--ex-purple);
    color: var(--white);
  }
  & > button:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

// ------------------------------------------------------------------- modal
export const Grow = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ModalScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(37, 34, 42, 0.55);
`;

export const ModalCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({ $wide, $variant }) => {
    if ($variant === 'criteria') return $wide ? '1120px' : '760px';
    if ($variant === 'feedback') return '620px';
    if ($variant === 'preview') return '720px';
    if ($variant === 'criteriaPreview') return '480px';
    return $wide ? '1040px' : '640px';
  }};
  min-height: 0;
  max-height: calc(100vh - 48px);
  background: var(--ex-surface);
  border-radius: ${({ $variant }) =>
    $variant ? '16px' : 'var(--ex-radius-card)'};
  box-shadow: var(--ex-shadow-modal);
  ${({ $variant }) =>
    $variant &&
    css`
      overflow: hidden;
    `}
  &:focus {
    outline: none;
  }
`;

export const ModalHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 24px 12px;
  h3 {
    ${exFont(700, 'xl')}
    margin: 0;
    color: var(--ex-ink);
  }
  p {
    ${exFont(400, 's')}
    margin: 4px 0 0;
    color: var(--ex-ink-2);
  }
  ${({ $variant }) =>
    $variant &&
    css`
      flex: 0 0 auto;
      h3 {
        font-size: ${$variant === 'preview' ? '20px' : '19px'};
        line-height: normal;
      }
      p {
        margin-top: 3px;
        font-size: 13px;
        font-weight: 500;
        line-height: normal;
        color: var(--ex-ink-2);
      }
      > button {
        width: 20px;
        height: 20px;
        padding: 0;
        color: var(--ex-ink);
      }
      > button:hover:not(:disabled) {
        background: none;
      }
    `}
  ${({ $variant }) =>
    ['criteria', 'feedback'].includes($variant) &&
    css`
      align-items: center;
      padding: 18px 24px;
      border-bottom: 1px solid var(--ex-line);
    `}
  ${({ $variant }) =>
    $variant === 'preview' &&
    css`
      padding: 20px 24px 4px;
    `}
  ${({ $variant }) =>
    $variant === 'criteriaPreview' &&
    css`
      padding: 20px 24px 6px;
    `}
`;

export const ModalEyebrow = styled.p`
  && {
    margin: 0 0 2px;
    font: 600 11.5px var(--ex-font-family);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--ex-ink-muted);
  }
`;

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 8px 24px 16px;
  overflow: auto;
  ${({ $variant }) =>
    $variant &&
    css`
      flex: 1 1 auto;
      min-height: 0;
      padding: 18px 24px;
    `}
  ${({ $variant }) =>
    $variant === 'criteria' &&
    css`
      gap: 16px;
    `}
  ${({ $variant }) =>
    $variant === 'preview' &&
    css`
      padding: 14px 24px 8px;
    `}
  ${({ $variant }) =>
    $variant === 'criteriaPreview' &&
    css`
      padding: 12px 24px 8px;
      gap: 0;
    `}
  ${({ $variant }) =>
    $variant === 'feedback' &&
    css`
      label {
        font-size: 13.5px;
      }
      input:not([type='file']) {
        height: 38px;
        padding: 0 10px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 400;
      }
    `}
`;

export const ModalFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid var(--ex-line);
  ${({ $variant }) =>
    $variant &&
    css`
      flex: 0 0 auto;
      gap: 12px;
      > button:not(:first-child),
      > button:only-child {
        min-height: 44px;
        padding: 0 26px;
        font-size: 15px;
        font-weight: 600;
        box-shadow: none;
      }
    `}
  ${({ $variant }) =>
    $variant === 'feedback' &&
    css`
      gap: 10px;
      padding-bottom: 18px;
      > button:not(:first-child) {
        padding: 0 20px;
      }
    `}
  ${({ $variant }) =>
    $variant === 'preview' &&
    css`
      padding-bottom: 20px;
      border-top: none;
      > button:only-child {
        padding: 13px 28px;
        line-height: normal;
      }
    `}
`;

export const CriteriaSourceRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  > button:first-child {
    min-height: 40px;
    padding: 0 18px;
    font-size: 14px;
    font-weight: 600;
  }
`;

export const SourcePickerRoot = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
  select {
    width: 100%;
    height: 40px;
    border-radius: 10px;
    padding: 0 ${({ $suggested }) => ($suggested ? '170px' : '36px')} 0 12px;
    font-size: 14px;
    font-weight: 500;
  }
  > span {
    position: absolute;
    top: 50%;
    right: 38px;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 11.5px;
    font-weight: 600;
    padding: 3px 10px;
  }
`;

export const PickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
`;

export const PickCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ $dashed }) => ($dashed ? '8px' : '10px')};
  padding: 22px 18px;
  border: ${({ $dashed }) => ($dashed ? '1.5px dashed' : '1px solid')}
    ${({ $over, $dashed }) =>
      $over ? 'var(--ex-purple)' : $dashed ? '#c9bff0' : 'var(--ex-line)'};
  border-radius: var(--ex-radius-card);
  background: ${({ $over }) =>
    $over ? 'var(--ex-purple-100)' : 'var(--ex-surface)'};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  border-radius: 14px;
  & > select {
    width: 100%;
    height: 40px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
  }
  p {
    ${exFont(400, 's')}
    line-height: 1.45;
    margin: 0;
    color: var(--ex-ink-strong);
  }
  /* "PDF, Word, Excel, image · 20 MB" (tplA 461). */
  & > p:last-of-type:not(:nth-of-type(2)) {
    ${exFont(400, 12)}
    margin-top: 2px;
    color: var(--ex-ink-2);
  }
  & > p b {
    font-weight: 400;
    white-space: nowrap;
  }
  & > p > button {
    font-size: inherit;
    line-height: inherit;
    vertical-align: baseline;
  }
`;

// "‹ Back · Add your own marking criteria" above the two cards (tplA 455).
export const PickHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  & > button:first-child {
    ${exFont(600, 14)}
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  & > button:first-child:hover:not(:disabled) {
    text-decoration: none;
  }
  & > span {
    ${exFont(600, 15)}
    color: var(--ex-ink);
  }
`;

export const PickIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${({ $upload }) =>
    $upload ? 'var(--ex-purple-100)' : 'var(--ex-surface-2)'};
  color: ${({ $upload }) =>
    $upload ? 'var(--ex-purple)' : 'var(--ex-ink-strong)'};
`;

export const PickActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  & > button:first-child {
    width: 100%;
    min-height: 40px;
    background: var(--ex-surface);
    border: 1.5px solid var(--ex-purple);
    color: var(--ex-purple);
    font-size: 14px;
    font-weight: 600;
  }
`;

export const PickTitle = styled.p`
  && {
    ${exFont(600, 15)}
    line-height: normal;
    color: var(--ex-ink);
  }
`;

// Import progress card (tplA 473–475): file row, then three 20px markers —
// green tick, purple spinner, grey ring (pending rows at 50%).
export const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px 20px;
  border: 1px solid var(--ex-line);
  border-radius: 14px;
  background: var(--ex-surface);
`;

export const StepLine = styled.div`
  ${({ $file }) => exFont($file ? 600 : 500, $file ? 14.5 : 14)}
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--ex-ink);
  opacity: ${({ $state }) => ($state === 'pending' ? 0.5 : 1)};
`;

export const StepDot = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  ${({ $state }) =>
    $state === 'done'
      ? css`
          background: var(--ex-ok);
        `
      : $state === 'active'
      ? css`
          border: 2.5px solid #e4d3fb;
          border-top-color: var(--ex-purple);
          animation: ex-step-spin 0.9s linear infinite;
        `
      : css`
          border: 2px solid var(--ex-line-strong);
        `}
  @keyframes ex-step-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// "Reading {file}…" while a comment bank / criteria file is read (tplA 665).
export const ReadingCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 22px 20px;
  border: 1px solid var(--ex-line);
  border-radius: 14px;
  & > span:last-child {
    ${exFont(500, 14.5)}
    color: var(--ex-ink);
  }
`;

// "JeddAI read {n} levels from {file}…" (tplA 478).
export const ReviewBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--ex-ok-bg);
  & > svg {
    flex: 0 0 auto;
    margin-top: 1px;
  }
  & > p {
    ${exFont(500, 13.5)}
    line-height: 1.5;
    margin: 0;
    color: #1f4d33;
  }
`;

export const PreviewHead = styled.div`
  min-height: 34px;
  display: flex;
  align-items: center;
  gap: 10px;
  span:first-child {
    ${exFont(700, 12)}
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--ex-ink-2);
  }
`;

// Clicking the previewed rubric starts editing a copy (tplA 484–485); while
// editing, the table's border turns purple.
export const PreviewClick = styled.div`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  ${({ $editing }) =>
    $editing &&
    css`
      & > div,
      & > table {
        border-color: var(--ex-purple) !important;
      }
    `}
`;

export const PreviewSaveButton = styled(Pill)`
  ${exFont(600, 13.5)}
  min-height: 34px;
  padding: 0 16px;
  border: 1.5px solid var(--ex-purple);
`;

export const PreviewEditButton = styled(Pill)`
  min-height: 34px;
  padding: 0 16px;
  border: 1.5px solid var(--ex-purple);
  color: var(--ex-purple);
  font-size: 13.5px;
  font-weight: 600;
`;

// ------------------------------------------------------------------ editors
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  ${({ $columns, $editing }) =>
    $columns &&
    css`
      min-width: ${110 + $columns * 200 + ($editing ? 56 : 0)}px;
    `}
  th,
  td {
    padding: ${({ $editing }) => ($editing ? '8px 10px' : '11px 14px')};
    border-bottom: 1px solid var(--ex-line);
    vertical-align: top;
    text-align: left;
    font-size: 14px;
    line-height: 1.5;
    color: #3d3847;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tbody td:first-child {
    font-size: 15px;
    font-weight: 700;
    line-height: normal;
    color: var(--ex-ink);
  }
  th {
    padding: 10px 14px;
    background: #fbfafd;
    font-size: 11.5px;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--ex-ink-2);
  }
  ${({ $columns }) =>
    $columns &&
    css`
      th button,
      td:first-child button {
        flex: 0 0 auto;
        width: 26px;
        height: 26px;
        padding: 0;
        border: 1px solid var(--ex-line);
        border-radius: 7px;
      }
      th:not(:first-child) {
        font-size: 13.5px;
        font-weight: 700;
        letter-spacing: normal;
        text-transform: none;
        color: var(--ex-royal);
      }
    `}
  ${({ $columns, $editing }) =>
    $columns &&
    $editing &&
    css`
      th:last-child button {
        width: 34px;
        height: 34px;
        border: 1.5px dashed var(--ex-purple);
        border-radius: var(--ex-radius-pill);
        color: var(--ex-purple);
      }
    `}
`;

export const GridScroll = styled.div`
  flex: 0 0 auto;
  overflow-x: auto;
  border: 1px solid var(--ex-line);
  border-radius: 12px;
`;

export const CellText = styled.textarea`
  font: 400 14px/1.45 var(--ex-font-family);
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  padding: 8px 10px;
  border: 1px solid
    ${({ $invalid }) => ($invalid ? 'var(--ex-bad)' : 'var(--ex-line-strong)')};
  border-radius: 8px;
  resize: vertical;
  color: var(--ex-ink);
  &:focus {
    outline: none;
    border-color: var(--ex-purple);
  }
`;

export const PointsInput = styled.input`
  ${exFont(500, 'xs')}
  box-sizing: border-box;
  width: 44px;
  padding: 4px;
  text-align: center;
  border: 1px solid
    ${({ $invalid }) => ($invalid ? 'var(--ex-bad)' : 'var(--ex-line-strong)')};
  border-radius: 6px;
  &:focus {
    outline: none;
    border-color: var(--ex-purple);
  }
`;

export const PointsRow = styled.div`
  ${exFont(500, 'xs')}
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  color: var(--ex-ink-muted);
`;

export const HeadInput = styled.input`
  font: 700 13.5px/1.3 var(--ex-font-family);
  color: inherit;
  box-sizing: border-box;
  flex: 1;
  width: 0;
  min-width: 0;
  height: 34px;
  padding: 0 10px;
  border: 1px solid var(--ex-line-strong);
  border-radius: 6px;
  &:focus {
    outline: none;
    border-color: var(--ex-purple);
  }
`;

export const SumLine = styled.p`
  ${exFont(600, 's')}
  margin: 0;
  color: ${({ $ok }) => ($ok ? 'var(--ex-ok-ink)' : 'var(--ex-warn-ink)')};
`;

export const OwnCancelButton = styled(Pill)`
  background: var(--ex-surface);
  border: 1px solid var(--ex-purple);
  color: var(--ex-purple);
`;

export const OwnGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid var(--ex-line);
  border-radius: 12px;
  > div:first-child {
    flex-wrap: nowrap;
    input {
      flex: 1;
      width: 0;
      min-width: 0;
      font-weight: 600;
    }
    button {
      flex: 0 0 auto;
      width: 15px;
      height: 15px;
      padding: 0;
    }
  }
  > div:last-child {
    margin-left: 14px;
  }
`;

export const OwnItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 14px;
  > span {
    color: #b9b5c0;
  }
  input {
    flex: 1;
    width: 0;
    min-width: 0;
  }
`;

// ---------------------------------------------------------------- feedback
export const TypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  flex-wrap: wrap;
  border-top: 1px solid var(--ex-line);
  &:first-child {
    border-top-color: transparent;
  }
  > span:first-child {
    ${exFont(500, 14.5)}
  }
  [role='radiogroup'] {
    padding: 3px;
    gap: 0;
    border: 1px solid var(--ex-line-strong);
    border-radius: 999px;
    background: white;
  }
  [role='radio'] {
    ${exFont(600, 13)}
    line-height: normal;
    padding: 6px 16px;
    border-radius: 999px;
    box-shadow: none;
    color: var(--ex-ink-strong);
  }
  [role='radio'][aria-checked='true'] {
    color: white;
    background: var(--ex-purple);
    box-shadow: none;
  }
`;

export const TypeQuestion = styled.span`
  ${exFont(500, 14.5)}
  line-height: normal;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 220px;
  color: var(--ex-ink);
  & > button {
    gap: 8px;
  }
`;

// The Feedback tab body: spacing comes from the design's margins, not the
// card's gap (tplA 678–690).
export const FbPane = styled.div`
  display: flex;
  flex-direction: column;
  & > ${Sub} {
    margin: 0 0 12px;
  }
`;

// "Choose what feedback…" · ⚙ Feedback preferences (tplA 678).
export const FbHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  & > p {
    ${exFont(600, 15)}
    line-height: normal;
    flex: 1;
    margin: 0 0 4px;
    color: var(--ex-ink);
  }
  & > button {
    ${exFont(600, 13.5)}
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    white-space: nowrap;
  }
  & > button:hover:not(:disabled) {
    text-decoration: none;
  }
`;

export const FbIntro = styled.p`
  ${exFont(400, 13.5)}
  line-height: normal;
  margin: 0 0 12px;
  color: var(--ex-ink-2);
`;

// Per-question config under a Yes row (tplA 679): one tinted box, rows
// split by rules; label · bank select · Preview · … · + Add your own.
export const ConfigBlock = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 12px;
  padding: 6px 16px;
  border: 1px solid var(--ex-line);
  border-radius: 12px;
  background: #fbfafd;
`;

export const ConfigRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 0;
  border-top: 1px solid var(--ex-line);
  ${ConfigBlock} > &:first-child {
    border-top-color: transparent;
  }
  opacity: ${({ $excluded }) => ($excluded ? 0.5 : 1)};
  & > span:first-of-type {
    ${exFont(700, 14)}
    min-width: 40px;
  }
  & > select {
    ${exFont(500, 14)}
    flex: 1;
    min-width: 200px;
    max-width: 380px;
    height: 38px;
    padding: 0 36px 0 12px;
    border-radius: 10px;
  }
  & > button:last-child,
  & > span:last-child > button {
    ${exFont(600, 13.5)}
    margin-left: auto;
    white-space: nowrap;
  }
  & > button:last-child:hover:not(:disabled) {
    text-decoration: none;
  }
`;

// "Preview" pill (tplA 679): 34px, 1px #ECEAF1, eye glyph.
export const PreviewPill = styled.button`
  ${exFont(600, 13)}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border: 1px solid var(--ex-line);
  border-radius: 999px;
  background: var(--ex-surface);
  color: var(--ex-purple);
  white-space: nowrap;
  cursor: pointer;
  &:disabled {
    color: var(--ex-ink-faint);
    cursor: default;
  }
`;

export const Tick = styled.button`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1.5px solid
    ${({ $on }) => ($on ? 'var(--ex-purple)' : 'var(--ex-ink-faint)')};
  background: ${({ $on }) => ($on ? 'var(--ex-purple)' : 'var(--ex-surface)')};
  color: var(--white);
  cursor: pointer;
`;

export const ChipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  &:empty {
    display: none;
  }
`;

// Chips + "+ Add" share one wrapping line (tplA 679 w.multi).
export const ChipLine = styled.div`
  display: flex;
  flex: 1 1 0%;
  min-width: 0;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export const ScChipRoot = styled.span`
  ${exFont(600, 13)}
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 32px;
  box-sizing: border-box;
  padding: 0 6px 0 12px;
  border-radius: 999px;
  border: 1.5px solid var(--ex-line);
  background: var(--ex-surface);
  color: var(--ex-ink);
  white-space: nowrap;
  cursor: grab;
  touch-action: none;
  & > button {
    width: 20px;
    height: 20px;
    margin-left: 2px;
    border-radius: 999px;
    align-items: center;
    justify-content: center;
  }
  & > button + button {
    margin-left: 0;
  }
  & > button:hover {
    background: var(--ex-purple-100);
  }
`;

// "+ Add ⌄" (tplA 679): dashed 32px pill.
export const AddDashed = styled.button`
  ${exFont(600, 13)}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  border: 1.5px dashed #c9bff0;
  border-radius: 999px;
  background: var(--ex-surface);
  color: var(--ex-purple);
  white-space: nowrap;
  cursor: pointer;
`;

// "+ Add" list (tplA 679): 270px card, search, items, "+ Add your own".
export const Popover = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 270px;
  max-height: 320px;
  overflow: auto;
  box-sizing: border-box;
  padding: 8px;
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: 12px;
  box-shadow: var(--ex-shadow-3);
  & > * {
    flex: 0 0 auto;
  }
  & > input {
    ${exFont(400, 13.5)}
    height: 36px;
    margin-bottom: 4px;
    padding: 0 10px;
    border-radius: 8px;
  }
  & > p {
    ${exFont(400, 13)}
    margin: 6px 10px;
    color: var(--ex-ink-2);
  }
`;

export const PopoverItem = styled.button`
  ${exFont(500, 13.5)}
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: none;
  text-align: left;
  color: var(--ex-ink);
  cursor: pointer;
  & > span:first-child {
    flex: 1;
  }
  &:hover {
    background: #f7f3fe;
  }
  &[data-own] {
    ${exFont(600, 13.5)}
    margin-top: 4px;
    padding: 9px 10px;
    border-top: 1px solid var(--ex-line);
    border-radius: 0;
    color: var(--ex-purple);
  }
`;

export const MineTag = styled.span`
  ${exFont(600, 10.5)}
  padding: 2px 6px;
  border-radius: 5px;
  background: var(--ex-ok-bg);
  color: var(--ex-ok-ink);
`;

export const Relative = styled.span`
  position: relative;
  display: inline-flex;
`;

export const Accordion = styled.div`
  margin-top: 18px;
  border-top: 1px solid var(--ex-line);
  padding-top: 16px;
  & > button > span:last-of-type {
    ${exFont(500, 13)}
    color: var(--ex-ink-2);
  }
`;

export const AccordionHead = styled.button`
  ${exFont(600, 15)}
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  color: var(--ex-ink);
  cursor: pointer;
`;

export const AccordionBody = styled.div`
  padding: 12px 0 16px;
`;

export const Sample = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: var(--ex-radius-card);
  background: var(--ex-surface-2);
  p {
    ${exFont(400, 's')}
    margin: 0;
    color: var(--ex-ink);
  }
`;

export const Caps = styled.p`
  && {
    ${exFont(600, 'xs')}
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--ex-ink-2);
  }
`;

export const Highlight = styled.span`
  background: var(--ex-warn-bg);
`;

// ------------------------------------------------------------------ extras
export const SectionTitle = styled.p`
  ${exFont(600, 14)}
  line-height: normal;
  margin: 0;
  color: var(--ex-ink);
`;

// Drop zone (tplA 709, 720): 1.5px dashed #c9bff0 on #FBF8FE, a 38px icon
// tile, 600 14px line and a 12.5px limits line.
export const DropZone = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1.5px dashed
    ${({ $over }) => ($over ? 'var(--ex-purple)' : '#c9bff0')};
  border-radius: 12px;
  background: ${({ $over }) => ($over ? '#f5eefe' : '#fbf8fe')};
  cursor: pointer;
  &:hover {
    background: #f5eefe;
  }
  > span:first-child {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: var(--ex-purple-100);
  }
  > div {
    flex: 1;
    min-width: 0;
  }
  > div > p:first-child {
    ${exFont(600, 14)}
    margin: 0;
    color: var(--ex-ink);
  }
  > div > p:first-child > button {
    font: inherit;
    vertical-align: baseline;
  }
  > div > p:first-child > button:hover:not(:disabled) {
    text-decoration: none;
  }
  > div > p:nth-child(2) {
    ${exFont(400, 12.5)}
    margin: 2px 0 0;
    color: var(--ex-ink-2);
  }
`;

export const ExemplarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  & + & {
    border-top: 1px solid var(--ex-line);
  }
`;

export const ExemplarName = styled.span`
  ${exFont(500, 's')}
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ex-ink);
`;
